import { Children, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { C } from './SiteChrome';

/**
 * One row of cards that scrolls sideways, with dots underneath and a slow
 * auto-advance that pauses the moment the reader touches it.
 *
 * Native scroll-snap rather than a transform track: it gives real touch
 * swiping on a phone for free, and can't desync the way a hardcoded percentage
 * step did (the track advanced by a third of the row regardless of how many
 * cards were actually visible, so on a phone showing one card per view it
 * shifted a third of a card and stopped mid-card).
 *
 * Shared by the testimonials and the blog so the two behave identically.
 * Whatever is passed as children becomes the slides, three across, two under
 * 900px and one under 640px.
 *
 * Pass `single` for one item per view at every width, or `row` for three across
 * (one on a phone): a slideshow of pictures rather than a row of cards. Use
 * `intervalMs={0}` to switch off auto-advance. `autoscroll={25}` drifts sideways
 * on its own at that many pixels a second instead, stops while the pointer is
 * over it or it is touched, and waits at the end before gliding back to the
 * start. It is skipped for anyone who has asked their device for less motion.
 * While it drifts, dots would mean nothing, so a thin progress bar shows how far
 * along it is, and the arrows step one card at a time and hold the drift a while.
 */
export const Carousel = ({ children, label = 'Page', intervalMs = 9000, single = false, row = false, autoscroll = 0 }) => {
    const slides = Children.toArray(children);
    const trackRef = useRef(null);
    const [page, setPage] = useState(0);
    const [pageCount, setPageCount] = useState(1);
    // Paused while the reader is interacting, so auto-advance never yanks a
    // card away mid-swipe.
    const pausedUntil = useRef(0);
    const hovering = useRef(false);
    const barRef = useRef(null);

    // A last page holding fewer cards than the rest is still a page, so this
    // rounds up: four posts three-across is 1.33 screenfuls, which is two
    // pages, not one. Rounding to nearest hid the fourth post entirely, with
    // no dot to reach it and an auto-advance that thought it had nowhere to
    // go. The 0.02 keeps a track that divides exactly from being tipped over
    // by a fraction of a pixel.
    const pagesOf = (el) => Math.max(1, Math.min(slides.length, Math.ceil(el.scrollWidth / el.clientWidth - 0.02)));

    // Which page is on screen. A short last page can't be scrolled a full
    // screenful, so the end of the track is read as the last page rather than
    // by dividing, which would leave the previous dot lit.
    const pageOf = (el, pages) => (
        el.scrollLeft >= el.scrollWidth - el.clientWidth - 2
            ? pages - 1
            : Math.min(pages - 1, Math.round(el.scrollLeft / el.clientWidth))
    );

    // And the reverse: never past the end of the track.
    const scrollToPage = (el, p) => el.scrollTo({
        left: Math.min(p * el.clientWidth, el.scrollWidth - el.clientWidth),
        behavior: 'smooth',
    });

    // clientWidth is 0 whenever the track is hidden or still laying out, and
    // scrollWidth / 0 is Infinity — which crashes Array.from below and blanks
    // the whole page. Bail out until it has a real width.
    const measure = () => {
        const el = trackRef.current;
        if (!el || !el.clientWidth) return;
        // The progress bar is moved directly, not through state, because it
        // changes on every frame of the drift and a re-render each time is waste.
        const span = el.scrollWidth - el.clientWidth;
        if (barRef.current) barRef.current.style.width = (span > 0 ? Math.max(6, Math.min(100, (el.scrollLeft / span) * 100)) : 100) + '%';
        const pages = pagesOf(el);
        setPageCount(pages);
        setPage(pageOf(el, pages));
    };

    useEffect(() => {
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [slides.length]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!intervalMs) return undefined;
        const id = setInterval(() => {
            const el = trackRef.current;
            if (!el || !el.clientWidth || Date.now() < pausedUntil.current) return;
            const pages = pagesOf(el);
            scrollToPage(el, (pageOf(el, pages) + 1) % pages);
        }, intervalMs);
        return () => clearInterval(id);
    }, [intervalMs]);

    useEffect(() => {
        if (!autoscroll) return undefined;
        const el = trackRef.current;
        if (!el) return undefined;
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
        let raf = 0;
        let last = 0;
        let pos = 0;
        let synced = false;
        let endedAt = 0;
        let visible = true;
        const io = 'IntersectionObserver' in window
            ? new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; })
            : null;
        if (io) io.observe(el);
        const tick = (t) => {
            raf = requestAnimationFrame(tick);
            const dt = last ? Math.min(t - last, 100) : 0;
            last = t;
            const max = el.scrollWidth - el.clientWidth;
            // Paused, off screen or nothing to scroll: forget the position, so
            // it is picked up from wherever the reader left it.
            if (!visible || hovering.current || Date.now() < pausedUntil.current || max <= 0) {
                synced = false;
                return;
            }
            if (!synced) { pos = el.scrollLeft; synced = true; }
            if (pos >= max - 1) {
                if (!endedAt) { endedAt = t; return; }
                if (t - endedAt < 2000) return;
                endedAt = 0;
                pausedUntil.current = Date.now() + 1500;   // let the glide back finish
                el.scrollTo({ left: 0, behavior: 'smooth' });
                return;
            }
            endedAt = 0;
            pos = Math.min(max, pos + (autoscroll * dt) / 1000);
            el.scrollLeft = pos;
        };
        raf = requestAnimationFrame(tick);
        return () => { cancelAnimationFrame(raf); if (io) io.disconnect(); };
    }, [autoscroll, slides.length]);

    // Measures rather than just reading the position, so a track that was
    // hidden or unlaid-out on mount picks up its page count here.
    const onScroll = () => measure();

    // Same chevron weight as the practice screens' Next arrow, in the site's
    // green. Plain button, no box, so it reads as navigation, not a control.
    const arrowStyle = {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: 'none', border: 'none', padding: 4, cursor: 'pointer',
        color: C.primary,
    };

    const goTo = (p) => {
        const el = trackRef.current;
        if (!el || !el.clientWidth) return;
        pausedUntil.current = Date.now() + 10000;
        scrollToPage(el, p);
    };

    // One card each way, wrapping round at the ends. Holds the drift for a few
    // seconds so the card can be read before it carries on.
    const step = (dir) => {
        const el = trackRef.current;
        if (!el || !el.clientWidth) return;
        pausedUntil.current = Date.now() + 6000;
        const first = el.firstElementChild;
        const width = first ? first.getBoundingClientRect().width : el.clientWidth;
        const max = el.scrollWidth - el.clientWidth;
        let target = el.scrollLeft + dir * width;
        if (dir > 0 && el.scrollLeft >= max - 2) target = 0;
        else if (dir < 0 && el.scrollLeft <= 2) target = max;
        el.scrollTo({ left: Math.max(0, Math.min(max, target)), behavior: 'smooth' });
    };

    return (
        <div>
            <div
                ref={trackRef}
                onScroll={onScroll}
                onPointerDown={() => { pausedUntil.current = Date.now() + 10000; }}
                onMouseEnter={() => { hovering.current = true; }}
                onMouseLeave={() => { hovering.current = false; }}
                className={(single ? 'mq-carousel-track mq-carousel-single' : row ? 'mq-carousel-track mq-carousel-row' : 'mq-carousel-track') + (autoscroll ? ' mq-carousel-free' : '')}
            >
                {slides.map((slide, idx) => (
                    <div key={idx} className="mq-carousel-slide">{slide}</div>
                ))}
            </div>

            {/* One page means nothing to page through, so the dots and arrows
                stay away. The arrows sit either side of the dots rather than over
                the cards: at one card per view on a phone, an arrow on the card
                edge would sit on top of the text. Both wrap round, the same way
                the auto-advance does. */}
            {pageCount > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 28 }}>
                <button
                    type="button"
                    onClick={() => (autoscroll ? step(-1) : goTo((page - 1 + pageCount) % pageCount))}
                    aria-label={`Previous ${label.toLowerCase()}`}
                    style={arrowStyle}
                >
                    <ChevronLeft size={28} strokeWidth={2.75} />
                </button>
                {autoscroll ? (
                    <div style={{ width: 140, height: 4, borderRadius: 999, background: '#cfe3d6', overflow: 'hidden' }} aria-hidden="true">
                        <div ref={barRef} style={{ width: '6%', height: '100%', borderRadius: 999, background: C.primary }} />
                    </div>
                ) : (
                    <>
                <div style={{ display: 'flex', gap: 9, justifyContent: 'center' }}>
                    {Array.from({ length: pageCount }, (_, d) => (
                        <button
                            key={d}
                            onClick={() => goTo(d)}
                            aria-label={`${label} ${d + 1}`}
                            aria-current={d === page}
                            style={{ width: 9, height: 9, borderRadius: 999, background: d === page ? C.primary : '#cfe3d6', cursor: 'pointer', border: 'none', padding: 0, transition: 'background 0.2s' }}
                        />
                    ))}
                </div>
                    </>
                )}
                <button
                    type="button"
                    onClick={() => (autoscroll ? step(1) : goTo((page + 1) % pageCount))}
                    aria-label={`Next ${label.toLowerCase()}`}
                    style={arrowStyle}
                >
                    <ChevronRight size={28} strokeWidth={2.75} />
                </button>
                </div>
            )}

            <style>{`
                .mq-carousel-track {
                    display: flex;
                    overflow-x: auto;
                    /* Room for a card's hover lift, which the horizontal
                       overflow would otherwise clip. */
                    padding: 6px 0 8px;
                    scroll-snap-type: x mandatory;
                    scrollbar-width: none;
                    -webkit-overflow-scrolling: touch;
                }
                .mq-carousel-track::-webkit-scrollbar { display: none; }
                .mq-carousel-slide {
                    flex: 0 0 33.3333%;
                    padding: 0 12px;
                    box-sizing: border-box;
                    scroll-snap-align: start;
                    /* Flex, so a card set to height 100% fills the tallest slide
                       in the row and the cards line up along the bottom. */
                    display: flex;
                }
                .mq-carousel-slide > * { width: 100%; }
                @media (max-width: 900px) { .mq-carousel-slide { flex-basis: 50%; } }
                @media (max-width: 640px) { .mq-carousel-slide { flex-basis: 100%; } }
                /* Snapping fights a slow drift a pixel at a time, so it is off. */
                .mq-carousel-free { scroll-snap-type: none; }
                .mq-carousel-single .mq-carousel-slide { flex: 0 0 100%; padding: 0; }
                .mq-carousel-row .mq-carousel-slide { flex: 0 0 33.3333%; padding: 0 5px; }
                @media (max-width: 640px) { .mq-carousel-row .mq-carousel-slide { flex-basis: 100%; padding: 0; } }
            `}</style>
        </div>
    );
};
